import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconPhoneCall,
  IconTransfer,
  IconMessage,
  IconPhoneOff,
  IconUsers,
  IconBan,
  IconPhoneX,
  IconUserMinus,
  IconCalendarEvent,
  IconHelpCircle,
} from '@tabler/icons-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@core-ui/Components/ui/tabs';
import type { SessionStats } from '../types/dashboard.types';
import { Flame } from 'lucide-react';

interface StatsCardsProps {
  stats: SessionStats;
  onClickStat: (type: string, filter: string) => void;
  fuegoLeadCount?: number | null;
  /**
   * Which tab is open. Controlled from the page ON PURPOSE: clicking a card
   * refetches, the page swaps itself for the loading skeleton, and this
   * component unmounts — an uncontrolled `defaultValue` would come back on
   * "outcomes" every time, throwing you out of the tab you were filtering from.
   */
  activeTab: string;
  onTabChange: (tab: string) => void;
}

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  iconBgColor: string;
  valueColor?: string;
  onSelected: (type: string, filter: string) => void;
  type: string;
  // Stable disposition/action id this card filters by (NOT the translated
  // label). Omit for cards that aren't a single filterable value (e.g. the
  // composite "fuego leads" or the aggregate "attempts" cards), which makes the
  // card non-filtering.
  filterValue?: string;
}

function StatCard ({ label, value, icon, iconBgColor, valueColor = 'text-foreground', onSelected, type, filterValue }: StatCardProps): React.JSX.Element {
  const isClickable = value !== 0 && filterValue !== undefined;
  return (
    <button
      disabled={!isClickable}
      className="bg-muted/40 rounded-xl border border-border p-4 min-h-[120px] text-left enabled:cursor-pointer enabled:hover:bg-muted transition disabled:cursor-default"
      onClick={() => {
        if (!isClickable) {
          return;
        }
        if (type === 'action') {
          onSelected('action', filterValue);
          onSelected('disposition', 'all');
        } else {
          onSelected('disposition', filterValue);
          onSelected('action', 'all');
        }
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`p-2 rounded-lg ${iconBgColor}`}>
          {icon}
        </div>
      </div>
      <div>
        <p className={`text-3xl font-bold ${valueColor}`}>{value}</p>
      </div>
    </button>
  );
}

export default function StatsCards ({ stats, onClickStat, fuegoLeadCount, activeTab, onTabChange }: StatsCardsProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="bg-card rounded-xl border border-border p-4">
      <TabsList className="mb-4">
        <TabsTrigger value="outcomes">{t('dashboard.statsCards.outcomes', 'Outcomes')}</TabsTrigger>
        <TabsTrigger value="dispositions">{t('dashboard.statsCards.dispositions', 'Dispositions')}</TabsTrigger>
        <TabsTrigger value="attempts">{t('dashboard.statsCards.attempts', 'Attempts')}</TabsTrigger>
      </TabsList>

      {/* Outcomes */}
      <TabsContent value="outcomes">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {/*
            "Connected" = calls that ended in a real conversation, taken from
            the dispositions. This card used to read `action="answered"`, which
            is written for every call that reaches the leg (voicemail pickups
            and wrong numbers included) and showed 37 on a 36-call session.
            It has no single filterValue — connected spans several dispositions
            — so it is not clickable; the Connected tab above the calls table
            applies the same filter.
          */}
          <StatCard
            label={t('dashboard.statsCards.connected', 'Connected')}
            value={stats.connected}
            icon={<IconPhoneCall className="h-5 w-5 text-green-500" />}
            iconBgColor="bg-green-500/10"
            valueColor="text-green-500"
            onSelected={onClickStat}
            type="action"
          />
          <StatCard
            label={t('dashboard.statsCards.transferred')}
            filterValue="transferred"
            value={stats.transferred}
            icon={<IconTransfer className="h-5 w-5 text-blue-500" />}
            iconBgColor="bg-blue-500/10"
            valueColor="text-blue-500"
            onSelected={onClickStat}
            type="action"
          />
          <StatCard
            label={t('dashboard.statsCards.setAppointments')}
            filterValue="set_appointment"
            value={stats.setAppointments}
            icon={<IconCalendarEvent className="h-5 w-5 text-purple-500" />}
            iconBgColor="bg-purple-500/10"
            valueColor="text-purple-500"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.leftVoicemail')}
            filterValue="left_vm"
            value={stats.leftVoicemail}
            icon={<IconMessage className="h-5 w-5 text-orange-500" />}
            iconBgColor="bg-orange-500/10"
            valueColor="text-orange-500"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.noAnswer')}
            filterValue="no_answer"
            value={stats.noAnswer}
            icon={<IconPhoneOff className="h-5 w-5 text-red-500" />}
            iconBgColor="bg-red-500/10"
            valueColor="text-red-500"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.wrongNumber')}
            filterValue="wrong_number"
            value={stats.wrongNumber}
            icon={<IconPhoneX className="h-5 w-5 text-gray-400" />}
            iconBgColor="bg-gray-500/10"
            onSelected={onClickStat}
            type="disposition"
          />
        </div>
      </TabsContent>

      {/* Dispositions */}
      <TabsContent value="dispositions">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatCard
            label={t('dashboard.statsCards.gkConversations')}
            filterValue="gatekeeper_conversation"
            value={stats.gatekeeperConversation}
            icon={<IconUsers className="h-5 w-5 text-cyan-500" />}
            iconBgColor="bg-cyan-500/10"
            valueColor="text-cyan-500"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.decisionMaker', 'Decision Maker')}
            filterValue="decision_maker_conversation"
            value={stats.decisionMakerConversation}
            icon={<IconUsers className="h-5 w-5 text-purple-500" />}
            iconBgColor="bg-purple-500/10"
            valueColor="text-purple-500"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.referred')}
            filterValue="referred_another_contact"
            value={stats.referredAnotherContact}
            icon={<IconUsers className="h-5 w-5 text-indigo-500" />}
            iconBgColor="bg-indigo-500/10"
            valueColor="text-indigo-500"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.noLongerWithCompany')}
            filterValue="no_longer_with_company"
            value={stats.noLongerWithCompany}
            icon={<IconUserMinus className="h-5 w-5 text-pink-500" />}
            iconBgColor="bg-pink-500/10"
            valueColor="text-pink-500"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.optOut')}
            filterValue="opt_out"
            value={stats.optOut}
            icon={<IconBan className="h-5 w-5 text-red-400" />}
            iconBgColor="bg-red-500/10"
            valueColor="text-red-400"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.phoneTree')}
            filterValue="phone_tree_no_connection"
            value={stats.phoneTreeNoConnection}
            icon={<IconPhoneOff className="h-5 w-5 text-purple-500" />}
            iconBgColor="bg-purple-500/10"
            valueColor="text-purple-500"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.phoneRefusal', 'Phone Refusal')}
            filterValue="phone_refusal"
            value={stats.phoneRefusal}
            icon={<IconPhoneX className="h-5 w-5 text-yellow-500" />}
            iconBgColor="bg-yellow-500/10"
            valueColor="text-yellow-500"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.refuseTransfer', 'Refuse Transfer')}
            filterValue="refuse_transfer"
            value={stats.refuseTransfer}
            icon={<IconTransfer className="h-5 w-5 text-orange-500" />}
            iconBgColor="bg-orange-500/10"
            valueColor="text-orange-500"
            onSelected={onClickStat}
            type="disposition"
          />
          <StatCard
            label={t('dashboard.statsCards.badNumberBusySignal', 'Bad Number / Busy')}
            filterValue="bad_number_busy_signal"
            value={stats.badNumberBusySignal}
            icon={<IconPhoneOff className="h-5 w-5 text-red-500" />}
            iconBgColor="bg-red-500/10"
            valueColor="text-red-500"
            onSelected={onClickStat}
            type="disposition"
          />
          {/*
            Calls the SDR never classified. These used to be dropped from the
            breakdown entirely, which is why the disposition counts never added
            up to the session's total. Showing them makes the gap visible — and
            it is a process signal worth seeing.
          */}
          <StatCard
            label={t('dashboard.statsCards.unclassified', 'Unclassified')}
            filterValue="default_action"
            value={stats.unclassified}
            icon={<IconHelpCircle className="h-5 w-5 text-slate-400" />}
            iconBgColor="bg-slate-400/10"
            valueColor="text-slate-400"
            onSelected={onClickStat}
            type="disposition"
          />
        </div>
      </TabsContent>

      {/* Attempts (aggregate figures — not per-disposition filters) */}
      <TabsContent value="attempts">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/*
            Total Calls counts calls, not people: a contact dialled twice
            counts twice, which is why it can exceed the number of rows in the
            table below. Unique Contacts is shown next to it so the difference
            is self-explanatory rather than looking like a bug.
          */}
          <StatCard
            label={t('dashboard.statsCards.totalCalls', 'Total Calls Made')}
            value={stats.totalCalls}
            icon={<IconPhoneCall className="h-5 w-5 text-blue-500" />}
            iconBgColor="bg-blue-500/10"
            onSelected={onClickStat}
            type="action"
          />
          <StatCard
            label={t('dashboard.statsCards.uniqueContacts', 'Unique Contacts')}
            value={stats.uniqueContacts}
            icon={<IconUsers className="h-5 w-5 text-indigo-500" />}
            iconBgColor="bg-indigo-500/10"
            onSelected={onClickStat}
            type="action"
          />
          <StatCard
            label={t('dashboard.statsCards.dialToConnect', 'Dial-to-Connect')}
            value={`${stats.dialToConnectRatio.toFixed(1)}%`}
            icon={<IconTransfer className="h-5 w-5 text-green-500" />}
            iconBgColor="bg-green-500/10"
            valueColor="text-green-500"
            onSelected={onClickStat}
            type="action"
          />
          <StatCard
            label={t('dashboard.statsCards.fuegoLeads')}
            value={fuegoLeadCount ?? 0}
            icon={<Flame className="h-5 w-5 text-amber-500" />}
            iconBgColor="bg-amber-500/10"
            valueColor="text-amber-500"
            onSelected={onClickStat}
            type="action"
          />
        </div>
      </TabsContent>
    </Tabs>
  );
}
